import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { PostDisbursementPayoutReportsBodyStatusCategoriesItem } from 'api';

import type { RootState } from 'reduxLocal/store';

type PaginationState = {
  includeCursor?: boolean;
  cursorBefore?: string;
  cursorAfter?: string;
  pageSize?: string;
};

export type FiltersState = {
  state?: PostDisbursementPayoutReportsBodyStatusCategoriesItem | 'All';
  accountNumber?: string;
  reasonCode?: string;
  currency?: string;
  amountFrom?: string;
  amountTo?: string;
  localInstrument?: string;
  businessUnitId?: string;
};

export type LocalState = {
  pagination: PaginationState;
  sort: string;
  filters: FiltersState;
  batchId?: string;
  key?: number;
};

export const initialState: LocalState = {
  pagination: {
    pageSize: '100'
  },
  sort: '-date',
  filters: {
    state: 'All',
    accountNumber: 'All',
    currency: 'All',
    reasonCode: undefined,
    amountFrom: undefined,
    amountTo: undefined,
    localInstrument: 'All',
    businessUnitId: 'All'
  }
};

const slice = createSlice({
  name: 'pagesPayouts',
  initialState,
  reducers: {
    updateFilters: (
      state,
      { payload }: PayloadAction<LocalState['filters']>
    ) => {
      state.filters = { ...state.filters, ...payload };
      state.pagination = { ...state.pagination };
    },
    setSort: (state, { payload }: PayloadAction<string>) => {
      state.sort = payload;
      state.pagination = { ...state.pagination };
    },
    setCursorBefore: (
      state,
      { payload }: PayloadAction<string | undefined>
    ) => {
      state.pagination.cursorBefore = payload;
      state.pagination.cursorAfter = undefined;
      state.pagination.includeCursor = undefined;
    },
    setCursorAfter: (state, { payload }: PayloadAction<string | undefined>) => {
      state.pagination.cursorAfter = payload;
      state.pagination.cursorBefore = undefined;
      state.pagination.includeCursor = undefined;
    },
    setIncludeCursor: (state, { payload }: PayloadAction<boolean>) => {
      state.pagination.includeCursor = payload;
    },
    setPageSize: (state, { payload }: PayloadAction<string | undefined>) => {
      state.pagination.pageSize = payload;
    },
    setKey: (state, { payload }: PayloadAction<number>) => {
      state.key = payload;
    },
    reset: () => initialState
  }
});

export const selectState = (state: RootState) => state.pagesPayouts;
export const selectSort = (state: RootState) => state.pagesPayouts.sort;
export const selectPagination = (state: RootState) =>
  state.pagesPayouts.pagination;
export const selectFilters = (state: RootState) => state.pagesPayouts.filters;

export const {
  reset,
  setSort,
  setCursorBefore,
  setCursorAfter,
  setIncludeCursor,
  setPageSize,
  updateFilters,
  setKey
} = slice.actions;

export default slice.reducer;
